import  { useCallback, useEffect, useRef, useState } from 'react';
import VBSearchInput from '@virkesborsen/vb-js-commons/lib/components/new/vbSearchInput';
import styled from "styled-components";
import VBLeaflet from '@virkesborsen/vb-js-commons/lib/components/new/vbLeaflet';
import VBLoadingScreen from "@virkesborsen/vb-js-commons/lib/components/vbLoadingScreen";
import VBDatePicker from '@virkesborsen/vb-js-commons/lib/components/new/vbDatePicker';
import VBRadioGroup from '@virkesborsen/vb-js-commons/lib/components/new/vbRadioGroup';
import { getProp, searchProps } from '../../api/services/property';
import { getShapeInfo, fetchGeojsonData, forestAgencyFellingBuffer } from '../../api/services/ad';
import { FellingClassesLegend } from '../minskog/helpers';
import { ErrorModal } from '../../modals';
import InfoIcon from './adcomponents/InfoIcon';
import { useAuctions } from '../../pages/auctions/context/AuctionsContext';
import { ZoomAndLayerButtons, LayerSelector } from '../mapComponents/MapComponents';

const PropListItem = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Step1 = () => {

    const { adData, changeAdData } = useAuctions();
    // Refs
    const searchInputRef = useRef(null);
    const mapRef = useRef(null)
    const adLayersGroupRef = useRef(new L.featureGroup([]));
    const searchTimeoutRef = useRef(null);
    const adLayerSectionsRef = useRef([]);
    const sectionsDataRef = useRef({});

    // State
    const [propSearchResults, setPropSearchResults] = useState([]);
    const [currentlySelectedPropertiesArray, setCurrentlySelectedPropertiesArray] = useState([]);
    const [allowMapEdit, setAllowMapEdit] = useState(true);
    const [searchedText, setSearchedText] = useState("");
    const [loading, setLoading] = useState(false);
    const [propertyLayers, setPropertyLayers] = useState({});
    const [loadingMessage] = useState(<h2 className="white w-100 text-center pt-1">Vänta...</h2>);

    const [startAdDate, setStartAdDate] = useState(adData.start_time ? new Date(adData.start_time) : null);
    const [endAdDate, setEndAdDate] = useState(adData.end_time ? new Date(adData.end_time) : null);
    const [startFellingDate, setStartFellingDate] = useState(adData.felling_start_time ? new Date(adData.felling_start_time) : null);
    const [endFellingDate, setEndFellingDate] = useState(adData.felling_end_time ? new Date(adData.felling_end_time) : null);

    const [adLayers, setAdLayers] = useState([]); 
    const [showFullTextSellingOption, setShowFullTextSellingOption] = useState(false);
    const [propArea, setPropArea] = useState(0);
    const [propAreaMap, setPropAreaMap] = useState({});
    const [legend, setLegend] = useState([]);
    const [showFellingButton, setShowFellingButton] = useState(true);
    const [showFellingApplicationButton, setShowFellingApplicationButton] = useState(true);
    const [mapTools, setMapTools] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [showLegend, setShowLegend] = useState({state: true,toggle:false});
    const [fellingLayers, setFellingLayers] = useState(null);
    const [fellingBufferLayers, setFellingBufferLayers] = useState(null);
    const [sectionsData, setSectionsData] = useState(null);
    const [errorModal, setErrorModal] = useState(false);

    // date-related error messages
    const [startAdErrorMessage, setStartAdErrorMessage] = useState(null);
    const [endAdErrorMessage, setEndAdErrorMessage] = useState(null);
    const [startFellingErrorMessage, setStartFellingErrorMessage] = useState(null);
    const [endFellingErrorMessage, setEndFellingErrorMessage] = useState(null);

    useEffect(() => {
        gtag('event', 'Created ad draft', {});
        gtag("event", "generate_lead", { currency: "SEK", value: 585 });

        const t = setTimeout(() => {
            if (adData.custom_area && mapRef.current) {
                setAllowMapEdit(false);

                const newAdLayers = [];

                L.geoJSON(adData.custom_area, {
                    onEachFeature: (feature, layer) => {
                        if (layer.feature?.geometry?.type === 'GeometryCollection') {
                            feature.geometries.forEach((geometry) => {
                                if (geometry.type === 'Point') {
                                    const marker = L.marker([geometry.coordinates[1], geometry.coordinates[0]], { type: 'marker' });
                                    marker.addTo(adLayersGroupRef.current);

                                    const popupContent = deleteMarker(marker, 'GeometryCollection');
                                    marker.bindPopup(popupContent);

                                    newAdLayers.push({ layer: marker });
                                } else {
                                    const polygonLayer = L.geoJSON(geometry, {
                                        color: "#ff00ff",
                                        fillOpacity: 0.8,
                                        weight: 2.7,
                                        dashArray: '10, 10',
                                        fillColor: "#ff00ff"
                                    });
                                    polygonLayer.addTo(adLayersGroupRef.current);
                                    newAdLayers.push({ layer: polygonLayer });
                                }
                            });
                        } else {
                            if (layer.options.alt === "Marker") {
                                layer.options.type = "marker";
                                if (adData.adLayers?.length > 0) {
                                    newAdLayers.push(...adData.adLayers);
                                }
                                const popupContent = deleteMarker(layer, 'Feature');
                                layer.bindPopup(popupContent);
                            } else {
                                layer.setStyle({
                                    color: "#ff00ff",
                                    fillOpacity: 0.8,
                                    weight: 2.7,
                                    dashArray: '10, 10',
                                    fillColor: "#ff00ff"
                                });
                            }
                            layer.addTo(adLayersGroupRef.current);
                        }
                    }
                });

                // Apply all layers at once
                setAdLayers(newAdLayers);

                mapRef.current.setLayers(adLayersGroupRef.current);
                adLayersGroupRef.current.addTo(mapRef.current.state.map);
                try {
                    mapRef.current.state.map.fitBounds(adLayersGroupRef.current.getBounds());
                } catch (e) { }
            }
        }, 200);

        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        if (refresh) setRefresh(false);
    }, [refresh]);

    useEffect(() => {
        adLayerSectionsRef.current = adLayers;
        sectionsDataRef.current = sectionsData;
    }, [adLayers, sectionsData]);
    // Helpers
    const formatSectionsData = ((inData) => {
        const sections = {};
        inData["data"].forEach((itm) => {
            sections[itm["nr"]] = { ...itm };
        });
        return sections;
    });

    const getLayerColor = ((sect_data) => {
        const felling_class = sect_data?.["felling_class"];
        return FellingClassesLegend[felling_class] ? FellingClassesLegend[felling_class].color : "#bb0000";
    });

    const getAdArrangementLabel = ((arrangement) => {
        if (arrangement === "gallring") return "Gallring";
        if (arrangement === "slutavverkning") return "Slutavverkning";
        if (arrangement === "vindfallen") return "Övrig avverkning";
        if (arrangement === "grot") return "Sälj biobränsle";
        return "";
    });

    const capitalizeFirstLetter = ((str) => {
        if (typeof str !== 'string' || !str.length) return str;
        return str[0] === str[0].toLowerCase() ? (str.charAt(0).toUpperCase() + str.slice(1)) : str;
    });

    // Map related (popups,add/remove, select, search

    const deleteMarker = useCallback((marker, type) => {
        const popupContent = document.createElement('div');
        const deleteButton = document.createElement('button');
        deleteButton.className = 'btn btn-sm btn-outline text-nowrap';
        deleteButton.textContent = 'Ta Bort';

        deleteButton.onclick = () => {
            if (adLayers.length > 0 && type === "Feature") {
                const updated = adLayers.filter(item => {
                    return !(item.layer.options?.type === 'marker' &&
                        Number(item.layer._latlng.lat.toFixed(6)) === Number(marker._latlng.lat.toFixed(6)) &&
                        Number(item.layer._latlng.lng.toFixed(6)) === Number(marker._latlng.lng.toFixed(6)));
                });
                setAdLayers(updated);
                changeAdData({ adLayers: updated });
                marker.remove();
            } else if (adData.custom_area?.geometries && type === "GeometryCollection") {
                const markerLatLng = marker.getLatLng();
                const updatedCustom = { ...adData.custom_area, geometries: adData.custom_area.geometries.filter(item => !(item.type === "Point" && item.coordinates[1] === markerLatLng.lat && item.coordinates[0] === markerLatLng.lng)) };
                changeAdData({ custom_area: updatedCustom });
                marker.remove();
            }
        };

        popupContent.appendChild(deleteButton);
        return popupContent;
    }, [adLayers, adData.custom_area, changeAdData]);

    const addLayerToAd = useCallback((layer, data) => {
        layer.addTo(adLayersGroupRef.current);
        mapRef.current.setLayers(adLayersGroupRef.current);
        adLayersGroupRef.current.addTo(mapRef.current.state.map);

        if (!layer.hasOwnProperty("_icon")) {
            layer.setStyle({
                color: "#ff00ff",
                fillOpacity: 0.8,
                weight: 2.7,
                dashArray: '10, 10'
            });
        }

        setAdLayers(prev => {
            const next = [...prev, { data, layer }];
            changeAdData({ adLayers: next });
            return next;
        });
        mapRef.current.setMode('view');
    }, [mapRef, changeAdData]);

    const deselectSection = useCallback((feature, layer) => {
        setAdLayers(prev => {
            const updated = prev.filter(item => item.layer._leaflet_id !== layer._leaflet_id);

            let color = "";
            if (sectionsDataRef.current && feature?.properties?.nr && sectionsDataRef.current[feature.properties.nr]) {
                color = getLayerColor(sectionsDataRef.current[feature.properties.nr]);
            } else {
                color = "#ffff00";
            }

            const originalStyle = { color, fillOpacity: 0, weight: 2.7, dashArray: null };
            if (layer.setStyle) layer.setStyle(originalStyle);

            changeAdData({ adLayers: updated });
            return updated;
        });
    }, [changeAdData]);

    const clearLayers = (() => {
        if (adLayers.length > 0) {
            adLayers.forEach(({ data, layer }) => {
                let color = "";
                if (data && typeof data.nr === 'number') color = getLayerColor(data);
                else if (data?.is_drawn) color = "";
                else color = "#ffff00";

                if (layer.setStyle) {
                    const originalStyle = { color, fillOpacity: 0, weight: 2.7, dashArray: null };
                    layer.setStyle(originalStyle);
                } else if (layer instanceof L.Marker) {
                    mapRef.current.state.map.removeLayer(layer);
                }
            });
        }
        setAdLayers([]);
        changeAdData({ adLayers: [] });
    });

    const deleteAll = (() => {
        if (adLayers.length > 0) {
            adLayers.forEach(({ layer }) => {
                if (layer.setStyle) {
                    const originalStyle = { color: null, fillOpacity: 0, weight: null, dashArray: null };
                    layer.setStyle(originalStyle);
                    mapRef.current.state.map.removeLayer(layer);
                } else if (layer instanceof L.Marker) {
                    mapRef.current.state.map.removeLayer(layer);
                }
            });
        }
        setAdLayers([]);
        changeAdData({ adLayers: [] });
    });

    const searchProp = ((val) => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        if (val.trim() === '') {
            setPropSearchResults([]);
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            const searchResult = await searchProps(val);
            if (searchResult.ok) {
                setPropSearchResults(
                    searchResult.data.filter(
                        (itm) => !currentlySelectedPropertiesArray.some((p) => p.registerenhet === itm.registerenhet)
                    )
                );
            } else {
                setErrorModal(true);
            }
        }, 400);
    });

    const updateMapWithSelectedProperty = (async (registerenhet) => {
        const propResponse = await getProp(registerenhet);
        if (propResponse.ok) {
            const propData = propResponse.data;

            setPropAreaMap(prev => ({ ...prev, [registerenhet]: propData.area }));
            setPropArea(prev => prev + propData.area);

            const borders = L.featureGroup([]);
            const propertyLayer = L.geoJson(propData.geojson, { color: "#ffffff" });
            propertyLayer.addTo(borders);
            propertyLayer.bringToBack();
            mapRef.current.setLayers(propertyLayer);
            borders.addTo(mapRef.current.state.map);

            setPropertyLayers((prev) => ({
                ...prev,
                [registerenhet]: { propertyLayer, json: propData.geojson },
            }));

            try {
                mapRef.current.state.map.fitBounds(propertyLayer.getBounds());
            } catch (e) { }
        } else {
            setErrorModal(true);
        }
    });

    const addProp = ((prop) => {
        if (currentlySelectedPropertiesArray.some(p => p.registerenhet === prop.registerenhet)) return;

        const propsArr = [...currentlySelectedPropertiesArray, prop];
        setCurrentlySelectedPropertiesArray(propsArr);
        setPropSearchResults([]);
        setSearchedText('');

        if (searchInputRef.current?.setValue) {
            searchInputRef.current.setValue(prop.beteckning);
        }
        updateMapWithSelectedProperty(prop.registerenhet);

        changeAdData({ "description_of_property": propsArr.map(itm => itm.beteckning).join(" & ") });
    });

    // Map click functions

    const handleDeleteClick = ((registerenhet) => {
        const updated = currentlySelectedPropertiesArray.filter((p) => p.registerenhet !== registerenhet);
        const { [registerenhet]: deletedRegisterenhet, ...rest } = propAreaMap;
        const deletedArea = deletedRegisterenhet || 0;

        const propertyLayer = propertyLayers[registerenhet]?.propertyLayer;
        if (propertyLayer) {
            deleteAll();
            propertyLayer.setStyle({ opacity: 0, fillOpacity: 0 });
            setPropAreaMap(rest);
            setPropArea(prev => prev - deletedArea);
        }

        const updatedPropertyLayers = { ...propertyLayers };
        delete updatedPropertyLayers[registerenhet];

        setCurrentlySelectedPropertiesArray(updated);
        setPropertyLayers(updatedPropertyLayers);

        const description = updated.length > 0 ? updated.map(itm => itm.beteckning).join(" & ") : "";
        changeAdData({ "description_of_property": description });
    });

    const handleShapeClick = (async (e, feature, layer) => {
        e.target.bringToFront();
        const geojson = e.target.toGeoJSON();
        
        if (adLayerSectionsRef.current.some((item) => item.layer._leaflet_id === layer._leaflet_id)) {
            deselectSection(feature, layer);
        }
        else {
            if (sectionsDataRef.current && feature?.properties?.nr && sectionsDataRef.hasOwnProperty(feature.properties.nr)) {
                const data = sectionsDataRef[feature.properties.nr];
                addLayerToAd(layer, data);
            } else {
                setLoading(true);
                const shapeInfo = await getShapeInfo(geojson);
                if (shapeInfo.ok) {
                    const data = { ...shapeInfo.data };
                    addLayerToAd(layer, data);
                    setLoading(false);
                } else {
                    setErrorModal(true);
                    setLoading(false);
                }
            }
        }

    });

    const handleAddMarkerButtonClick = ((type) => {
        const addMarkerFunc = (event) => {
            const { lat, lng } = event.latlng;
            const marker = new L.marker([lat, lng], { type: 'marker' });
            const popupContent = deleteMarker(marker, "Feature");
            marker.bindPopup(popupContent);
    
            if (type === "Feature") {
                addLayerToAd(marker, null);
            } else if (type === "GeometryCollection") {
                marker.addTo(adLayersGroupRef.current);
                mapRef.current.setLayers(adLayersGroupRef.current);
                adLayersGroupRef.current.addTo(mapRef.current.state.map);

                const newMarkerFeature = {
                    type: "Point",
                    coordinates: [Number(lng.toFixed(6)), Number(lat.toFixed(6))]
                };
                const updatedAdData = JSON.parse(JSON.stringify(adData));
                updatedAdData.custom_area.geometries.push(newMarkerFeature);
                changeAdData({ custom_area: updatedAdData.custom_area });
            }

            mapRef.current.state.map.getContainer().style.cursor = 'auto';
            mapRef.current.state.map.off('click', addMarkerFunc);
        };

        mapRef.current.state.map.getContainer().style.cursor = 'crosshair';
        mapRef.current.state.map.on('click', addMarkerFunc);
    });

    const finishDraw = (async (e) => {
        const layer = e.layer;
        const shapeInfo = await getShapeInfo(layer.toGeoJSON());
        if (shapeInfo.ok) {
            const data = { ...shapeInfo.data, is_drawn: true };
            layer.on('click', (ev) => {
                handleShapeClick(ev, layer.toGeoJSON(), layer);
            });
            addLayerToAd(layer, data);
            setLoading(false);
        } else {
            setErrorModal(true);
        }
    });

    const handleShowFellingApplicationsClick = (async () => {
        setLoading(true);
        handleHideFellingClass();

        const sections = L.featureGroup([]);
        const prms = Object.values(propertyLayers).map(async (lyr) => {
            const forestAgencyResp = await forestAgencyFellingBuffer(lyr.json);
            if (forestAgencyResp.ok) {
                const json = forestAgencyResp.data;
                setShowFellingApplicationButton(false);

                L.geoJSON(json, {
                    interactive: true,
                    style: () => ({ color: "#ffff00" }),
                    onEachFeature: (feature, layer) => {
                        layer.bringToFront();
                        layer.on('click', (e) => {
                            handleShapeClick(e, feature, layer, mapRef.current.state.drawn);
                        });
                        layer.addTo(sections);
                    },
                });

                mapRef.current.setLayers(sections);
                sections.addTo(mapRef.current.state.map);
                setFellingBufferLayers(sections);
            } else {
                setErrorModal(true);
            }
        });

        await Promise.all(prms);
        setLoading(false);
        adLayersGroupRef.current.bringToFront();
    });

    const handleShowFellingClassClick = (async () => {
        setLoading(true);
        handleHideFellingClass();

        const uniqueLegendData = new Set();
        const sections = L.featureGroup([]);

        const prms = Object.values(propertyLayers).map(async (itm) => {
            const geojsonDataResp = await fetchGeojsonData(itm.json);
            try {
                if (geojsonDataResp.ok) {
                    const json = geojsonDataResp.data;
                    const formatted = formatSectionsData(json);
                    setShowFellingButton(false);

                    if (json["geoJson"]) {
                        L.geoJSON(json["geoJson"], {
                            interactive: true,
                            style: (feature) => {
                                const sdata = formatted[feature["properties"]["nr"]];
                                const legendLabel = sdata?.["felling_class"];
                                const color = getLayerColor(sdata);
                                if (legendLabel && !uniqueLegendData.has(legendLabel)) uniqueLegendData.add(legendLabel);
                                return { color, legendLabel };
                            },
                            onEachFeature: (feature, layer) => {
                                layer.bringToFront();
                                layer.on('click', (e) => handleShapeClick(e, feature, layer));
                                layer.addTo(sections);
                            },
                        });

                        mapRef.current.setLayers(sections);
                        sections.addTo(mapRef.current.state.map);
                        setSectionsData(formatted);
                        setFellingLayers(sections);
                    }
                } else {
                    setErrorModal(true);
                }
            } catch (err) {
                console.error('Error loading geojson:', err);
                setErrorModal(true);
            }
        });

        await Promise.all(prms);
        adLayersGroupRef.current.bringToFront();
        setLoading(false);
        setLegend(Array.from(uniqueLegendData));
    });

    const handleHideFellingClass = (() => {
        if (fellingLayers) {
            mapRef.current.state.map.removeLayer(fellingLayers);
            setFellingLayers(null);
        }
        if (fellingBufferLayers) {
            mapRef.current.state.map.removeLayer(fellingBufferLayers);
            setFellingBufferLayers(null);
        }

        const sections = L.featureGroup([]);
        if (adLayers.length !== 0) {
            adLayers.forEach((adLayer) => adLayer.layer.addTo(sections));
            sections.addTo(mapRef.current.state.map);
        }

        setShowFellingButton(true);
        setShowFellingApplicationButton(true);
    });

    const handleSellingOptionChange = ((selectedOption) => {
        changeAdData({ type: selectedOption });
    });

    // Dates
    const handleDateChange = ((date, type) => {
        if (!date) {
            console.error('invalid date:', date);
        }
        const convert_date = date.toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (type === 'startAd') {
            const error = date < today ? 'Du kan bara börja från och med idag.' : null;
            setStartAdErrorMessage(error);
            if (!error) {
                changeAdData({ start_time: convert_date });
                setStartAdDate(date);
            }
        }

        if (type === 'endAd') {
            const minEndDate = new Date(startAdDate);
            minEndDate.setDate(minEndDate.getDate() + 1);
            const endAdDateLocal = new Date(date);

            const error = endAdDateLocal.getTime() < minEndDate.getTime() ? '*Slutdatum måste vara minst 1 dag från startdatumet.' : null;
            setEndAdErrorMessage(error);
            if (!error) {
                changeAdData({ end_time: convert_date });
                setEndAdDate(endAdDateLocal);
            }
        }

        if (type === 'startFelling') {
            const endAdDateLocal = new Date(endAdDate);
            let error = null;
            if (date < endAdDateLocal) {
                error = '*Startdatumet måste vara minst detsamma som eller senare än slutdatumet för annonsperioden.';
            }
            setStartFellingErrorMessage(error);
            if (!error) {
                changeAdData({ felling_start_time: convert_date });
                setStartFellingDate(date);
            }
        }

        if (type === 'endFelling') {
            const minEndDate = new Date(startFellingDate);
            minEndDate.setDate(minEndDate.getDate() + 1);

            const error = date.getTime() < minEndDate.getTime() ? '*Slutdatum måste vara minst 1 dag från startdatum.' : null;
            setEndFellingErrorMessage(error);
            if (!error) {
                changeAdData({ felling_end_time: convert_date });
                setEndFellingDate(date);
            }
        }
    });

    const handleDateDeliveryChange = ((date, type) => {
        if (!date) {
            console.error('Invalid date:', date);
            return;
        }
        const convert_date = date.toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' });

        if (type === 'startFelling') {
            const endAdDateLocal = new Date(endAdDate);
            const error = endAdDateLocal.getTime() > new Date(date).getTime() ? 'Startdatum för avverkning kan inte vara före annonsens slutdatum.' : null;

            setStartFellingErrorMessage(error);
            if (!error) {
                changeAdData({ felling_start_time: convert_date });
                setStartFellingDate(date);
            }

            // Set endFellingDate to startFelling year
            const endFellingDateLocal = new Date(date);
            setStartFellingDate(date);
            setEndFellingDate(endFellingDateLocal);

            changeAdData({
                felling_start_time: convert_date,
                felling_end_time: endFellingDateLocal.toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' })
            });
        }
    });

    const handleReadMoreSellingOptionClick = (() => {
        setShowFullTextSellingOption((s) => !s);
    });

    const deleteTheWholeAd = (() => {
        setLoading(true);
        changeAdData({
            adLayers: [],
            forestWithSections: null,
            total_volume: null,
            total_area_ha: null,
            longitude: null,
            latitude: null,
            area: null,
            geometryFile: null,
            geoCollection: null,
            description_of_property: null,
            ground_preparation: false,
            felling_application_responsible: null,
            fsc_certified: null,
            pefc_certified: null,
            visible_draft: false,
            assortment: null,
            region: null,
            muni: null,
            attachments: [],
            custom_area: null
        });
        window.location.reload();
    });

    // Derived values
    const filteredProperties = currentlySelectedPropertiesArray;
    const adType = Window.react_data.adTypes.find((itm) => itm.value === adData.type);
    const ad_type_name = adType ? adType.label.toLowerCase() : "";
    const fellingClassesOrder = ['S1', 'S2', 'G1', 'G2', 'R', 'K', 'IA', 'R1'];

    // Render helpers

    const propertyView = (() => (
        <div className='mt-3'>
            {filteredProperties.length > 0 && (
                <p className='mb-2 fw-medium neutral-charcoal'>Sälj ditt virke:</p>
            )}
            {filteredProperties.map((itm) => (
                <PropListItem key={itm.registerenhet} className="my-2 neutral-charcoal">
                    <div className='d-flex align-items-center' style={{ cursor: 'auto' }}>
                        {itm.beteckning}
                        <img
                            src={`${Window.static_path}img/icons/delete.svg`}
                            className='neutral-charcoal-filter ms-2 mb-1'
                            style={{ cursor: 'pointer' }}
                            height='18px'
                            width='18px'
                            alt='Delete Icon'
                            onClick={() => handleDeleteClick(itm.registerenhet)}
                        />
                    </div>
                </PropListItem>
            ))}
        </div>
    ));

    // Render

    return (
        <div className='' data-step='1'>
            <VBLoadingScreen
                show_loading={loading}
                disable_reload_timeout={true}
                loading_text={loadingMessage}
            />

            <div className='row flex-md-row justify-content-between d-flex flex-column-reverse align-items-center mt-5'>
                <div className='col-12 col-md-8 '>
                    <p className='mb-2 fw-medium neutral-charcoal'>Fastighetsbeteckning / fastighetsbeteckningar:</p>
                    <div className='col-lg-8 col-12 position-relative rounded shadow p-2' style={{ zIndex: 1020 }}>
                        {allowMapEdit || !adData.description_of_property ? (
                            <VBSearchInput
                                ref={searchInputRef}
                                placeholder='Sök eller eg: Sevedstorp 1:8'
                                onChange={(val) => { setSearchedText(val.trim()); searchProp(val); }}
                                searchResult={
                                    (searchedText !== '')
                                        ? ((propSearchResults && propSearchResults.length > 0)
                                            ? propSearchResults.map(prop => (
                                                <p key={prop.registerenhet} className="option w-100 ps-2" onClick={() => { addProp(prop); }}>{prop.beteckning}</p>
                                            ))
                                            : (<p>Inga träffar på den sökningen.</p>)
                                        )
                                        : null
                                }
                            />
                        ) : (
                            <p className="fs-8 m-2">{adData.description_of_property}</p>
                        )}
                    </div>

                    <div>
                        <ul style={{ listStyleType: 'none', padding: 0 }}>{propertyView()}</ul>
                    </div>

                    <div>
                        {adData.description_of_property !== null && (
                            <div>
                                <button className='n-btn btn-charcoal' onClick={deleteTheWholeAd}>Börja om din annons</button>

                                {!allowMapEdit && (
                                    <>
                                        {adData.custom_area?.geometries?.length > 0 && (
                                            <button className="n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1" onClick={() => handleAddMarkerButtonClick("GeometryCollection")}>Placera Markör</button>
                                        )}
                                        {adData.custom_area?.features?.length > 0 && (
                                            <button
                                                className="n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1"
                                                onClick={() => {
                                                    setAdLayers(adData?.adLayers || []);
                                                    handleAddMarkerButtonClick("Feature");
                                                }}
                                            >
                                                Placera Markör
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className='col-12 col-md-4 mb-4 mb-md-0'>
                    <div className='rounded-3 bg-neutral-stone-300 p-3'>
                        <div className='d-flex flex-column'>
                            <p className='mb-2 neutral-charcoal fw-medium'>Behöver du hjälp?</p>
                            <p className='neutral-charcoal n-fs-7 mb-2'>Du når oss via telefon eller mail:</p>
                            <a className='neutral-charcoal text-decoration-underline n-fs-7 mb-2' href='tel:08-33 99 44'>08-33 99 44</a>
                            <a className='neutral-charcoal text-decoration-underline n-fs-7' href='mailto: info@virkesborsen.se'> info@treebula.se</a>
                        </div>
                    </div>
                </div>
            </div>

            {filteredProperties.length > 0 && allowMapEdit ? (
                <div className='row'>
                    <p className='mb-1 neutral-charcoal mt-3 fw-medium'>Vänligen välj:</p>
                    {adData.arrangement !== "gallring" ? <p className='mb-1 mt-1 neutral-charcoal'><span className='fw-medium'>Visa avverkningsanmälningar:</span> För att se avverkningsanmälningar och välja ett område.</p> : null}
                    <p className='mb-1 neutral-charcoal'><span className='fw-medium'>Rita:</span> för att skapa en eller flera områden direkt på kartan.</p>
                    <p className='mb-1 neutral-charcoal'><span className='fw-medium'>Placera Markör:</span> om du vill markera en plats på kartan.</p>
                    <p className='mb-1 neutral-charcoal'><span className='fw-medium'>Visa avdelningar:</span> För att se automatiskt genererade avdelningar baserat på öppen data och för att välja ett område. </p>
                    <p className='mb-1 neutral-charcoal'>
                        <span className='fw-medium'>Radera allt:</span> för att rensa kartan.
                        För att avmarkera en markerad avdelning klickar du på den markerade avdelningen igen.
                    </p>

                    <div className='row'>
                        <div className='mt-2 row col-12 col-lg-10 col-xl-8'>
                            {adData.arrangement !== "gallring" && (
                                <div className='col-12 col-md-6 col-lg-4'>
                                    {showFellingApplicationButton ? (
                                        <button className="n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1" onClick={handleShowFellingApplicationsClick}>Visa Avverkningsanmälningar</button>
                                    ) : (
                                        <button className="n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1" onClick={handleHideFellingClass}>Dölj Avverkningsanmälningar</button>
                                    )}
                                </div>
                            )}

                            <div className='col-4 col-md-4 col-lg-2'>
                                <button
                                    className="rita-button n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1"
                                    onClick={() => { setMapTools({ "state": true, "data": "draw" }); mapRef.current.setMode('edit'); mapRef.current.startDraw('Polygon'); }}
                                >
                                    Rita
                                </button>
                            </div>

                            <div className='col-6 col-md-4 col-lg-3'>
                                <button className="n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1" onClick={() => handleAddMarkerButtonClick("Feature")}>Placera Markör</button>
                            </div>

                            {propArea < 500 && (
                                <div className='col-6 col-md-4 col-lg-3'>
                                    {showFellingButton ? (
                                        <button className="n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1" onClick={handleShowFellingClassClick}>Visa Avdelningar</button>
                                    ) : (
                                        <button className="n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1" onClick={handleHideFellingClass}>Dölj Avdelningar</button>
                                    )}
                                </div>
                            )}

                            {adLayers.length > 0 && (
                                <div className='col-6 col-md-4 col-lg-2'>
                                    <button className="n-btn btn-charcoal btn-outline text-nowrap btn-sm m-1" onClick={clearLayers}>Radera allt</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            <div className='mt-3' style={{ height: '500px', position: 'relative' }}>
                {(adData.description_of_property === null || adData.description_of_property === "") ? (
                    <div className='d-flex align-items-center justify-content-center px-4' style={{ backgroundColor: 'black', position: 'absolute', width: '100%', height: '500px', zIndex: 1010, borderRadius: "15px" }}>
                        <p className='n-fs-5 white'>Ange fastighetsbeteckning ovan för att se kartan</p>
                    </div>
                ) : null}

                <VBLeaflet ref={mapRef} id='map_auctions' baseMap='Mapbox Satelite' finishDraw={finishDraw} /> 
                <ZoomAndLayerButtons mapRef={mapRef} mapTools={mapTools} setMapTools={(val) => { setMapTools(val) }} showLegend = {showLegend} setShowLegend={setShowLegend}/>
               
                {mapTools && <LayerSelector mapRef = {mapRef} mapTools={mapTools} setMapTools = {(val)=>{setMapTools(val)}} setRefresh={(val)=>setRefresh(val)} setLoading={(val)=>setLoading(val)}/>}
               

                {/* Legend for felling class */}
                <div className={`${showLegend.toggle ? 'd-flex' : 'd-none'} flex-column bg-neutral-cloud shadow rounded z-1010 position-absolute p-1 mt-3`} style={{ width: '250px', right: '10px', bottom: '120px' }}>
                    <div className='bg-white caption p-2'>
                        Huggningsklasser
                        {legend
                            ?.sort((a, b) => fellingClassesOrder.indexOf(a) === -1 ? 1 : fellingClassesOrder.indexOf(b) === -1 ? -1 : fellingClassesOrder.indexOf(a) - fellingClassesOrder.indexOf(b))
                            .map(i => (
                                <div key={i} className='d-flex mb-1'>
                                    <div className='me-1' style={{ backgroundColor: FellingClassesLegend[i] ? FellingClassesLegend[i].color : '#bb0000', height: '16px', minWidth: '16px' }}></div>
                                    <b>{i}</b>: {FellingClassesLegend[i] ? FellingClassesLegend[i].text : 'Okänd'}
                                </div>
                            ))}
                    </div>
                </div>

            </div>

            {/* Type summary */}
            <div className='mt-5'>
                <p className='neutral-charcoal'>Typ av annons: <span className='fw-medium'>{getAdArrangementLabel(adData.arrangement)}</span>.</p>
            </div>

            {/* 1. Hur vill du sälja ditt virke? */}
            <div className='row flex-md-row justify-content-between mt-5'>
                <div className='col-12 col-md-6'>
                    <div className='d-flex'>
                        <p className='neutral-charcoal fw-medium'>1. Hur vill du sälja ditt virke?</p>
                        <InfoIcon
                            title="Hur vill du sälja ditt virke?"
                            instructions={[
                                <div key="instr-1">
                                    <p><span className='fw-medium'>Ange önskad försäljningsform.</span> Är du osäker hjälper vi dig gärna med att förklara vilka olika alternativ som finns.</p>
                                    <p><span className='fw-medium'>Avverkningsuppdrag:</span> Är den vanligaste försäljningsformen. Här får du antingen anbud som pris per sortiment eller prislista + eventuella tillägg. Kostnaden för att avverka och ta hand om ditt virke får du som en fast avverkningskostnad eller som en lista baserad på medelstam.</p>
                                    <p><span className='fw-medium'>Leveransrotköp:</span> Med denna försäljningsform får du ett nettopris inkl. avverkningskostnader oavsett kvalité och sortiment. Exempel: 450kr/ m³fub.</p>
                                    <p><span className='fw-medium'>Leveransvirke:</span> När du eller någon annan avverkar och skogsbolaget köper virket vid bilväg. Här får du också oftast prislista + eventuella tillägg eller fast pris per sortiment.</p>
                                    <p><span className='fw-medium'>Rotpost:</span> När skogen är stämplad av en stämplingsförrätare och en stämplingslängd finns. Då får du ett total pris inkl. avverkningskostnad. Exempel: 1 000 000 kr.</p>
                                </div>
                            ]}
                        />
                    </div>

                    <VBRadioGroup
                        options={Window.react_data.adTypes.map(adType => ({
                            value: adType.value,
                            label: capitalizeFirstLetter(adType.label)
                        }))}
                        initial={adData.type}
                        onChange={handleSellingOptionChange}
                    />
                </div>

                <div className='d-md-block d-none mt-3 mt-md-0 col-12 col-md-5'>
                    <div className='rounded-3 bg-neutral-stone-300 p-3'>
                        <p className='mb-2'><span className='fw-medium'>Ange önskad försäljningsform.</span> Är du osäker hjälper vi dig gärna med att förklara vilka olika alternativ som finns.</p>
                        {showFullTextSellingOption && (
                            <div>
                                <p className='mb-2'><span className='fw-medium'>Leveransrotköp:</span> Med denna försäljningsform får du ett nettopris inkl. avverkningskostnader oavsett kvalité och sortiment. Exempel: 450kr/ m³fub.</p>
                                <p className='mb-2'><span className='fw-medium'>Leveransvirke:</span> När du eller någon annan avverkar och skogsbolaget köper virket vid bilväg. Här får du också oftast prislista + eventuella tillägg eller fast pris per sortiment.</p>
                                <p className='mb-2'><span className='fw-medium'>Rotpost:</span> När skogen är stämplad av en stämplingsförrätare och en stämplingslängd finns. Då får du ett total pris inkl. avverkningskostnad. Exempel: 1 000 000 kr.</p>
                            </div>
                        )}
                        <button className="n-btn btn-outline btn-charcoal" onClick={handleReadMoreSellingOptionClick}>
                            {showFullTextSellingOption ? "Läs mindre" : "Läs mer"}
                        </button>
                    </div>
                </div>
            </div>

            {/* 2. Annonseringsperiod */}
            <div className='row flex-md-row justify-content-between mt-5'>
                <div className='col-12 col-md-6'>
                    <div className='d-flex'>
                        <p className='neutral-charcoal fw-medium'>2. Annonseringsperiod:</p>
                        <InfoIcon
                            title="Annonseringsperiod:"
                            instructions={[
                                <p key="instr-2" className='mb-0 neutral-charcoal'><span className='fw-medium'>Annonseringsperiod:</span> Här väljer du hur lång tid din annons ska vara publicerad hos Treebula. Det är den tid som virkesköparna har på sig att lämna anbud. Vi rekommenderar att annonsen ligger publicerad i 3 till 4 veckor så att köparna har tillräckligt med tid.</p>
                            ]}
                        />
                    </div>

                    <div className='row flex-md-row justify-content-between'>
                        <div className='col-8 col-lg-5'>
                            <p className='mb-0 neutral-charcoal'>Från</p>
                            <VBDatePicker onChange={(date) => handleDateChange(date, 'startAd')} selectedDate={startAdDate} />
                            {startAdErrorMessage && (<p className="red-alert error-message">{startAdErrorMessage}</p>)}
                        </div>
                        <div className='col-8 col-lg-5 mt-3 mt-lg-0'>
                            <p className='mb-0 neutral-charcoal'>Till</p>
                            <VBDatePicker onChange={(date) => handleDateChange(date, 'endAd')} selectedDate={endAdDate} />
                            {endAdErrorMessage && <p className="red-alert error-message">{endAdErrorMessage}</p>}
                        </div>
                    </div>
                </div>

                <div className='d-md-block d-none mt-3 mt-md-0 col-12 col-md-5'>
                    <div className='rounded-3 bg-neutral-stone-300 p-3'>
                        <p className='mb-0 neutral-charcoal'><span className='fw-medium'>Annonseringsperiod:</span> Här väljer du hur lång tid din annons ska vara publicerad hos Treebula. Det är den tid som virkesköparna har på sig att lämna anbud. Vi rekommenderar att annonsen ligger publicerad i 3 till 4 veckor så att köparna har tillräckligt med tid.</p>
                    </div>
                </div>
            </div>

            {/* Felling period (delivery or default) */}
            {ad_type_name === 'leveransvirke' ? (
                <div className='row flex-md-row justify-content-between mt-5'>
                    <div className='col-12 col-md-6'>
                        <p className='mb-2 fw-medium neutral-charcoal'>Leveransperiod:</p>
                        <div className='row flex-md-row justify-content-between'>
                            <div className='col-8 col-lg-5'>
                                <p className='mb-0'>Upphämtningsdatum</p>
                                <VBDatePicker onChange={(date) => handleDateDeliveryChange(date, 'startFelling')} selectedDate={startFellingDate} value={startFellingDate} />
                                {startFellingErrorMessage && (<p className="error-message red-alert">{startFellingErrorMessage}</p>)}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className='row flex-md-row justify-content-between mt-5'>
                    <div className='col-12 col-md-6'>
                        <div className='d-flex'>
                            <p className='mb-2 fw-medium neutral-charcoal'>3. Avverkningsperiod:</p>
                            <InfoIcon
                                title="Avverkningsperiod:"
                                instructions={[
                                    <p key="instr-3" className='mb-0'><span className='fw-medium'>Avverkningsperiod:</span> Här anger du hur lång tid köparen som du eventuellt väljer har på sig att avverka skogen. Vi rekommenderar 2 till 5 år.</p>
                                ]}
                            />
                        </div>

                        <div className='row flex-md-row justify-content-between'>
                            <div className='col-8 col-lg-5'>
                                <p className='mb-0'>Från</p>
                                <VBDatePicker onChange={(date) => handleDateChange(date, 'startFelling')} selectedDate={startFellingDate} />
                                {startFellingErrorMessage && (<p className="error-message red-alert">{startFellingErrorMessage}</p>)}
                            </div>
                            <div className='col-8 col-lg-5 mt-3 mt-lg-0'>
                                <p className='mb-0'>Till</p>
                                <VBDatePicker onChange={(date) => handleDateChange(date, 'endFelling')} selectedDate={endFellingDate} isEndDate />
                                {endFellingErrorMessage && <p className="error-message red-alert">{endFellingErrorMessage}</p>}
                            </div>
                        </div>
                    </div>

                    <div className='d-md-block d-none mt-3 mt-md-0 col-12 col-md-5'>
                        <div className='rounded-3 bg-neutral-stone-300 p-3'>
                            <p className='mb-0'><span className='fw-medium'>Avverkningsperiod:</span> Här anger du hur lång tid köparen som du eventuellt väljer har på sig att avverka skogen. Vi rekommenderar 2 till 5 år.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Responsible for felling application */}
            {adData.arrangement !== "gallring" && (
                <div className='row flex-md-row justify-content-between mt-5'>
                    <div className='col-12 col-md-6'>
                        <div className='d-flex'>
                            <p className='col-md-12 col-6 fw-medium neutral-charcoal'>{`${ad_type_name === 'leveransvirke' ? '3.' : '4.'} Vem är ansvarig för avverkningsanmälan?`}</p>
                            <InfoIcon
                                title="Vem är ansvarig för avverkningsanmälan?"
                                instructions={[
                                    <div key="instr-4">
                                        <p className='mb-2 neutral-charcoal'><span className='fw-medium'>Jag:</span> Du som skogsägare utför själv avverkningsanmälan via skogsstyrelsen.</p>
                                        <p className='mb-2 neutral-charcoal'><span className='fw-medium'>Köparen:</span> Personen som du eventuellt väljer som köpare upprättar avverkningsanmälan åt dig.</p>
                                        <p className='mb-0'><span className='fw-medium'>Finns redan:</span> Om en avverkningsanmälan redan finns väljer du detta alternativ.</p>
                                    </div>
                                ]}
                            />
                        </div>

                        <VBRadioGroup
                            options={[
                                { label: 'Jag', value: 'seller' },
                                { label: 'Köparen', value: 'buyer' },
                                { label: 'Finns Redan', value: 'exists' },
                            ]}
                            initial={adData.felling_application_responsible}
                            onChange={(val) => { changeAdData({ felling_application_responsible: val }); }}
                        />
                    </div>

                    <div className='d-md-block d-none mt-3 mt-md-0 col-12 col-md-5'>
                        <div className='rounded-3 bg-neutral-stone-300 p-3'>
                            <p className='mb-2 neutral-charcoal'><span className='fw-medium'>Jag:</span> Du som skogsägare utför själv avverkningsanmälan via skogsstyrelsen.</p>
                            <p className='mb-2 neutral-charcoal'><span className='fw-medium'>Köparen:</span> Personen som du eventuellt väljer som köpare upprättar avverkningsanmälan åt dig.</p>
                            <p className='mb-0'><span className='fw-medium'>Finns redan:</span> Om en avverkningsanmälan redan finns väljer du detta alternativ.</p>
                        </div>
                    </div>
                </div>
            )}

            <ErrorModal show={!!errorModal} title={errorModal?.title} message={errorModal?.message} onToggle={() => setErrorModal(false)} />
        </div>
    );
};

export default Step1;

